import { type FC, type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_VISIBLE_REPLIES_COUNT,
  OPTIMISTIC_REPLY_ID_PREFIX,
  REPLIES_BATCH_SIZE,
  REPLIES_PAGE_SIZE,
  focusElementWithoutScroll,
  getVisibleRepliesState,
  handleCommentPaste,
  isOptimisticReplyId,
  mergeRepliesById,
  moveCaretToEnd,
  normalizeCommentBody,
  resetCommentEditor,
  scrollCaretIntoView,
  scrollElementIntoViewOnNextFrame,
  syncCommentDraftFromElement,
} from '@/lib/comments';
import { type Comment as CommentData, type GetNodeCommentsResponse, type NodeComment } from '@/types/comments';
import { CommentShowMoreSeparator } from '@/components/inspector/comments/comment-show-more-separator';
import { useFetchCommentRepliesMutation } from '@/hooks/comments/useFetchCommentRepliesMutation';
import { useCreateNodeCommentMutation } from '@/hooks/comments/useCreateNodeCommentMutation';
import { useUpdateNodeCommentMutation } from '@/hooks/comments/useUpdateNodeCommentMutation';
import { ModalId, useModalStore, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { CommentItem } from '@/components/inspector/comments/comment-item';
import { useCommentStore } from '@/hooks/data/comments/useCommentStore';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useAccount } from '@/hooks/data/account/useAccount';
import { useCapabilities } from '@/hooks/useCapabilities';
import { isDefined } from '@/utilities/guards';
import { QueryKey } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const Comment: FC<{
  comment: NodeComment;
  readOnly?: boolean;
  openCommentMenuId?: string | null;
  setOpenCommentMenuId: (id: string | null) => void;
  nodeId?: string;
  scrollContainerRef?: MutableRefObject<HTMLDivElement | null>;
}> = ({ comment, readOnly, openCommentMenuId, setOpenCommentMenuId, nodeId, scrollContainerRef }) => {
  const { canCommentPrivately } = useCapabilities();
  const [localBody, setLocalBody] = useState(comment.comment.body ?? '');
  const [localUpdatedAt, setLocalUpdatedAt] = useState(comment.comment.updatedAt);
  const [localPrivate, setLocalPrivate] = useState<boolean>(canCommentPrivately && (comment.private ?? false));
  const [replyDraft, setReplyDraft] = useState('');
  const [pendingReplyCloseId, setPendingReplyCloseId] = useState<string | null>(null);
  const [visibleRepliesCount, setVisibleRepliesCount] = useState(DEFAULT_VISIBLE_REPLIES_COUNT);
  const [pinnedDialogKey, setPinnedDialogKey] = useState<string | null>(null);
  // Any dialog opened from this comment — the privacy confirmation, a delete confirmation — moves
  // the pointer off it, so the hover that was revealing its actions is lost and the whole row
  // would fade out behind the dialog. Keyed on the dialog rather than on which action opened it,
  // so a new confirmation gets this for free. Entries flagged `isClosing` still count: confirming
  // closes the dialog and starts the mutation in the same click, and the mutation's own in-flight
  // styling only takes over once that render lands.
  const isPinnedByOwnDialog = useModalStore((state) =>
    isDefined(pinnedDialogKey) ? state.stack.some((entry) => entry.key === pinnedDialogKey) : false,
  );

  const {
    isAuthenticatedUser,
    isGuest,
    profile: {
      user: { id: userId, name: userName },
    },
  } = useAccount();

  const tComments = useTranslations('inspector.comments');
  const tDialogs = useTranslations('dialogs');
  const openModal = useOpenModal();

  const editingCommentId = useCommentStore((state) => state.editingCommentId);
  const activeReplyComposerCommentId = useCommentStore((state) => state.activeReplyComposerCommentId);
  const draftByCommentId = useCommentStore((state) => state.draftByCommentId);
  const deletingCommentId = useCommentStore((state) => state.deletingCommentId);
  const setEditingCommentId = useCommentStore((state) => state.setEditingCommentId);
  const setActiveReplyComposerCommentId = useCommentStore((state) => state.setActiveReplyComposerCommentId);
  const setCommentDraft = useCommentStore((state) => state.setCommentDraft);
  const clearCommentDraft = useCommentStore((state) => state.clearCommentDraft);

  const editorRef = useRef<HTMLDivElement | null>(null);
  const replyEditorRef = useRef<HTMLDivElement | null>(null);
  const replyDraftItemRef = useRef<HTMLDivElement | null>(null);
  const replyDraftCreatedAtRef = useRef<string | null>(null);
  const replyDraftIdRef = useRef<string | null>(null);
  const rootItemRef = useRef<HTMLDivElement | null>(null);
  const replyEditorRefsById = useRef<Record<string, MutableRefObject<HTMLDivElement | null>>>({});
  const replyItemRefsById = useRef<Record<string, MutableRefObject<HTMLDivElement | null>>>({});
  const wasEditingRootRef = useRef(false);

  const rootComment = useMemo(
    () => ({ ...comment.comment, body: localBody, updatedAt: localUpdatedAt }),
    [comment.comment, localBody, localUpdatedAt],
  );
  const commentAuthorRole = comment.comment.createdBy?.role;
  const canDeleteComment = (author: CommentData['createdBy']) =>
    !readOnly && Boolean(userId) && (userId === author?.id || (isAuthenticatedUser && author?.role === 'guest'));

  const wasReplyComposerOpenRef = useRef(false);

  const isRootTombstone = Boolean(comment.tombstone?.isDeleted || comment.comment.tombstone?.isDeleted);
  const isEditingRoot = !isRootTombstone && editingCommentId === comment.commentId;
  const isReplyComposerOpen = !isRootTombstone && activeReplyComposerCommentId === comment.commentId;
  const isDeletingRoot = deletingCommentId === comment.commentId;
  const normalizedBody = normalizeCommentBody(localBody);
  const normalizedRootDraft = normalizeCommentBody(draftByCommentId[comment.commentId] ?? normalizedBody);
  const normalizedReplyDraft = normalizeCommentBody(replyDraft);

  const shareConfig = useShareConfig();

  const mergedReplies = useMemo(
    () => (isRootTombstone ? [] : mergeRepliesById(comment.latestReplies)),
    [comment.latestReplies, isRootTombstone],
  );

  const isEditingReply = useMemo(
    () => Boolean(editingCommentId && mergedReplies.some((reply) => reply.id === editingCommentId)),
    [editingCommentId, mergedReplies],
  );

  const effectiveRepliesCount = isRootTombstone ? 0 : Math.max(comment.repliesCount, mergedReplies.length);

  const { visibleReplies, nextLoadRepliesCount, shouldShowMoreRepliesAction } = useMemo(
    () =>
      getVisibleRepliesState({
        replies: mergedReplies,
        repliesCount: effectiveRepliesCount,
        visibleRepliesCount,
        loadMoreBatchSize: REPLIES_BATCH_SIZE,
      }),
    [effectiveRepliesCount, mergedReplies, visibleRepliesCount],
  );

  const isFirstVisibleReplyBeingEdited = Boolean(
    shouldShowMoreRepliesAction && editingCommentId === visibleReplies[0]?.id,
  );

  const draftReply = useMemo(() => {
    if (!isReplyComposerOpen || isRootTombstone || readOnly) {
      return null;
    }

    const createdAt = replyDraftCreatedAtRef.current ?? new Date().toISOString();
    const draftReplyId = replyDraftIdRef.current ?? `${OPTIMISTIC_REPLY_ID_PREFIX}${comment.commentId}-${Date.now()}`;

    return {
      id: draftReplyId,
      body: normalizedReplyDraft,
      createdBy: userId ? { id: userId, name: userName ?? 'You' } : null,
      createdAt,
      updatedAt: createdAt,
      parentId: comment.commentId,
    } as CommentData;
  }, [comment.commentId, isReplyComposerOpen, isRootTombstone, normalizedReplyDraft, readOnly, userId, userName]);

  const renderedRepliesCount = visibleReplies.length + (draftReply ? 1 : 0);

  useEffect(() => {
    setLocalBody(comment.comment.body ?? '');
    setLocalUpdatedAt(comment.comment.updatedAt);
    setLocalPrivate(canCommentPrivately && (comment.private ?? false));
  }, [comment.comment.body, comment.comment.updatedAt, comment.private, canCommentPrivately]);

  useEffect(() => {
    setVisibleRepliesCount(DEFAULT_VISIBLE_REPLIES_COUNT);
    setPendingReplyCloseId(null);
    replyDraftCreatedAtRef.current = null;
    replyDraftIdRef.current = null;
    setActiveReplyComposerCommentId((currentActiveReplyComposerCommentId) =>
      currentActiveReplyComposerCommentId === comment.commentId ? null : currentActiveReplyComposerCommentId,
    );
    resetCommentEditor({
      setDraft: setReplyDraft,
      editorRef: replyEditorRef,
    });
  }, [comment.commentId, setActiveReplyComposerCommentId]);

  useEffect(() => {
    if (wasEditingRootRef.current && !isEditingRoot) {
      clearCommentDraft(comment.commentId);
    }

    wasEditingRootRef.current = isEditingRoot;
  }, [clearCommentDraft, comment.commentId, isEditingRoot]);

  useEffect(() => {
    if (!isRootTombstone) {
      return;
    }

    replyDraftCreatedAtRef.current = null;
    replyDraftIdRef.current = null;
    setPendingReplyCloseId(null);
    clearCommentDraft(comment.commentId);
    setEditingCommentId((currentEditingCommentId) =>
      currentEditingCommentId === comment.commentId ? null : currentEditingCommentId,
    );
    setActiveReplyComposerCommentId((currentActiveReplyComposerCommentId) =>
      currentActiveReplyComposerCommentId === comment.commentId ? null : currentActiveReplyComposerCommentId,
    );

    if (openCommentMenuId === comment.commentId) {
      setOpenCommentMenuId(null);
    }
  }, [
    clearCommentDraft,
    comment.commentId,
    isRootTombstone,
    openCommentMenuId,
    setActiveReplyComposerCommentId,
    setEditingCommentId,
    setOpenCommentMenuId,
  ]);

  const { updateNodeCommentMutation: updateCommentMutation } = useUpdateNodeCommentMutation();

  const { createNodeCommentMutation } = useCreateNodeCommentMutation();

  useEffect(() => {
    if (wasReplyComposerOpenRef.current && !isReplyComposerOpen) {
      resetCommentEditor({
        setDraft: setReplyDraft,
        editorRef: replyEditorRef,
      });
      replyDraftCreatedAtRef.current = null;
      replyDraftIdRef.current = null;
    }

    wasReplyComposerOpenRef.current = isReplyComposerOpen;
  }, [isReplyComposerOpen]);

  useEffect(() => {
    if (!pendingReplyCloseId) {
      return;
    }

    const hasOptimisticReplyInThread = mergedReplies.some((reply) => reply.id === pendingReplyCloseId);

    if (!hasOptimisticReplyInThread) {
      return;
    }

    closeReplyComposer();
    setPendingReplyCloseId(null);
  }, [mergedReplies, pendingReplyCloseId]);

  const queryClient = useQueryClient();

  const { fetchCommentRepliesMutation: fetchRepliesMutation } = useFetchCommentRepliesMutation();

  const hasNodeId = typeof nodeId === 'string';
  const canSubmitReply =
    !readOnly &&
    !isRootTombstone &&
    hasNodeId &&
    normalizedReplyDraft.trim().length !== 0 &&
    !createNodeCommentMutation.isPending;

  const updateRepliesForParentThreadInCache = ({
    targetNodeId,
    parentId,
    updateThread,
  }: {
    targetNodeId: string;
    parentId: string;
    updateThread: (thread: NodeComment) => NodeComment;
  }) => {
    const queryCache = queryClient.getQueryCache();
    const queries = queryCache.findAll({
      queryKey: [QueryKey.NodeCommentList, targetNodeId, shareConfig?.id],
      exact: false,
    });

    queries.forEach((query) => {
      queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            comments: page.comments.map((thread) => {
              if (thread.commentId !== parentId || thread.tombstone?.isDeleted) {
                return thread;
              }

              return updateThread(thread);
            }),
          })),
        };
      });
    });
  };

  const getReplyEditorRef = (replyId: string) => {
    if (!replyEditorRefsById.current[replyId]) {
      replyEditorRefsById.current[replyId] = { current: null } as MutableRefObject<HTMLDivElement | null>;
    }

    return replyEditorRefsById.current[replyId];
  };

  const getReplyItemRef = (replyId: string) => {
    if (!replyItemRefsById.current[replyId]) {
      replyItemRefsById.current[replyId] = { current: null } as MutableRefObject<HTMLDivElement | null>;
    }

    return replyItemRefsById.current[replyId];
  };

  const startEditingComment = ({ commentId, body }: { commentId: string; body: string }) => {
    const normalizedInitialBody = normalizeCommentBody(body);

    setActiveReplyComposerCommentId(null);
    setCommentDraft(commentId, normalizedInitialBody);
    setEditingCommentId((currentEditingCommentId) => {
      if (currentEditingCommentId && currentEditingCommentId !== commentId) {
        clearCommentDraft(currentEditingCommentId);
      }

      return commentId;
    });
    setOpenCommentMenuId(null);
  };

  const cancelEditingComment = (commentId: string) => {
    clearCommentDraft(commentId);
    setEditingCommentId((currentEditingCommentId) =>
      currentEditingCommentId === commentId ? null : currentEditingCommentId,
    );
  };

  // Changing a comment's privacy in either direction is a meaningfully different action than
  // editing its body alone — contributors immediately gain or lose access to it — so confirm
  // before committing, regardless of which way it's changing.
  const confirmPrivacyChange = ({ nextPrivate, onConfirm }: { nextPrivate: boolean; onConfirm: () => void }) => {
    const modalKey = openModal(ModalId.Confirm, {
      content: nextPrivate ? tDialogs('makeCommentPrivateConfirmation') : tDialogs('makeCommentPublicConfirmation'),
      confirmButtonDataCy: nextPrivate
        ? 'confirm-make-comment-private-continue'
        : 'confirm-make-comment-public-continue',
      dismissButtonDataCy: nextPrivate ? 'confirm-make-comment-private-dismiss' : 'confirm-make-comment-public-dismiss',
      confirmButtonVariant: 'info',
      onConfirm,
    });

    setPinnedDialogKey(modalKey);
  };

  const handleSaveCommentEdit = ({
    commentId,
    originalBody,
    isPrivate,
    onSuccess,
  }: {
    commentId: string;
    originalBody: string;
    isPrivate: boolean;
    onSuccess?: (updatedComment: CommentData) => void;
  }) => {
    const normalizedOriginalBody = normalizeCommentBody(originalBody);
    const normalizedDraftValue = normalizeCommentBody(draftByCommentId[commentId] ?? normalizedOriginalBody);
    const isDraftEmpty = normalizedDraftValue.trim().length === 0;
    const isDraftUnchanged = normalizedDraftValue === normalizedOriginalBody;

    if (isDraftEmpty || isDraftUnchanged || updateCommentMutation.isPending) {
      return;
    }

    updateCommentMutation.mutate(
      {
        commentId,
        body: normalizedDraftValue,
        nodeId: comment.nodeId,
        // Privacy is owned by the toggle, which confirms and commits on its own — the editor only
        // ever carries the current value through so saving a body edit cannot flip it.
        private: isPrivate,
      },
      {
        onSuccess: (updatedComment) => {
          cancelEditingComment(commentId);
          onSuccess?.(updatedComment);
        },
      },
    );
  };

  // Toggling privacy from the comment's menu (no body change involved) — same confirmation
  // flow as changing it while editing, just without touching the body.
  const handleTogglePrivacy = () => {
    if (updateCommentMutation.isPending) {
      return;
    }

    const nextPrivate = !localPrivate;

    const applyPrivacyChange = () => {
      updateCommentMutation.mutate(
        {
          commentId: comment.commentId,
          body: normalizedBody,
          nodeId: comment.nodeId,
          private: nextPrivate,
        },
        {
          onSuccess: (updatedComment) => {
            setLocalPrivate(nextPrivate);
            setLocalBody(updatedComment.body ?? '');
            setLocalUpdatedAt(updatedComment.updatedAt);
          },
        },
      );
    };

    confirmPrivacyChange({ nextPrivate, onConfirm: applyPrivacyChange });
  };

  const handleReplyDeleteSuccess = (replyId: string) => {
    clearCommentDraft(replyId);
    setEditingCommentId((currentEditingCommentId) =>
      currentEditingCommentId === replyId ? null : currentEditingCommentId,
    );

    if (openCommentMenuId === replyId) {
      setOpenCommentMenuId(null);
    }
  };

  const showMoreReplies = () => {
    if (fetchRepliesMutation.isPending) {
      return;
    }

    const nextVisibleRepliesCount = Math.min(effectiveRepliesCount, visibleRepliesCount + REPLIES_BATCH_SIZE);
    const targetVisibleRepliesCount =
      nextVisibleRepliesCount === visibleRepliesCount ? visibleRepliesCount : nextVisibleRepliesCount;
    const repliesNeeded = targetVisibleRepliesCount - mergedReplies.length;

    if (nextVisibleRepliesCount !== visibleRepliesCount) {
      setVisibleRepliesCount(nextVisibleRepliesCount);
    }

    if (repliesNeeded <= 0) {
      return;
    }

    const currentOldestLoadedReplyOffset = Math.max(comment.repliesCount - mergedReplies.length, 0);

    if (currentOldestLoadedReplyOffset <= 0) {
      return;
    }

    const repliesToFetch = Math.min(REPLIES_PAGE_SIZE, repliesNeeded, currentOldestLoadedReplyOffset);

    if (repliesToFetch <= 0) {
      return;
    }

    const nextOffset = currentOldestLoadedReplyOffset - repliesToFetch;

    fetchRepliesMutation.mutate(
      {
        commentId: comment.commentId,
        limit: repliesToFetch,
        offset: nextOffset,
        shareId: shareConfig?.id,
      },
      {
        onSuccess: (data) => {
          if (!nodeId) {
            return;
          }

          const { replies } = data;
          if (!replies.length) {
            return;
          }

          const parentId = replies[0]?.parentId;
          if (!parentId) {
            return;
          }

          const queryCache = queryClient.getQueryCache();
          const queries = queryCache.findAll({ queryKey: [QueryKey.NodeCommentList, nodeId, shareConfig?.id] });

          queries.forEach((query) => {
            queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
              if (!oldData) {
                return oldData;
              }

              return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  comments: page.comments.map((nodeComment) => {
                    if (nodeComment.commentId !== parentId) {
                      return nodeComment;
                    }

                    return {
                      ...nodeComment,
                      latestReplies: mergeRepliesById(replies, nodeComment.latestReplies),
                    };
                  }),
                })),
              };
            });
          });
        },
      },
    );
  };

  const openReplyComposer = () => {
    if (readOnly || !hasNodeId || isRootTombstone) {
      return;
    }

    setOpenCommentMenuId(null);

    if (editingCommentId) {
      clearCommentDraft(editingCommentId);
      setEditingCommentId(null);
    }

    // If the reply composer is already open and user clicks on the reply again, focus the composer
    if (isReplyComposerOpen && replyEditorRef.current) {
      focusElementWithoutScroll(replyEditorRef.current);
      moveCaretToEnd(replyEditorRef.current);
      scrollCaretIntoView(replyEditorRef.current, { defer: true });
      if (replyDraftItemRef.current) {
        scrollElementIntoViewOnNextFrame(replyDraftItemRef.current, {
          container: scrollContainerRef?.current,
          marginBottom: 12,
        });
      }
      return;
    }

    replyDraftCreatedAtRef.current = new Date().toISOString();
    replyDraftIdRef.current = `${OPTIMISTIC_REPLY_ID_PREFIX}${comment.commentId}-${Date.now()}`;
    setActiveReplyComposerCommentId(comment.commentId);
  };

  const closeReplyComposer = () => {
    replyDraftCreatedAtRef.current = null;
    replyDraftIdRef.current = null;
    resetCommentEditor({
      setDraft: setReplyDraft,
      editorRef: replyEditorRef,
    });

    setActiveReplyComposerCommentId((currentActiveReplyComposerCommentId) =>
      currentActiveReplyComposerCommentId === comment.commentId ? null : currentActiveReplyComposerCommentId,
    );
  };

  const handleSubmitReply = () => {
    if (!canSubmitReply || !nodeId || createNodeCommentMutation.isPending) {
      return;
    }

    const submittedReplyBody = normalizedReplyDraft;
    const submittedReplyCreatedAt = replyDraftCreatedAtRef.current ?? new Date().toISOString();
    const submittedReplyId =
      replyDraftIdRef.current ?? `${OPTIMISTIC_REPLY_ID_PREFIX}${comment.commentId}-${Date.now()}`;
    setPendingReplyCloseId(submittedReplyId);
    const optimisticReply: CommentData = {
      id: submittedReplyId,
      body: submittedReplyBody,
      createdBy: userId ? { id: userId, name: userName ?? 'You' } : null,
      createdAt: submittedReplyCreatedAt,
      updatedAt: submittedReplyCreatedAt,
      parentId: comment.commentId,
    };

    void queryClient.cancelQueries({
      queryKey: [QueryKey.NodeCommentList, nodeId, shareConfig?.id],
      exact: false,
    });

    updateRepliesForParentThreadInCache({
      targetNodeId: nodeId,
      parentId: comment.commentId,
      updateThread: (thread) => {
        if (thread.latestReplies.some((reply) => reply.id === optimisticReply.id)) {
          return thread;
        }

        return {
          ...thread,
          latestReplies: mergeRepliesById(thread.latestReplies, [optimisticReply]),
          repliesCount: thread.repliesCount + 1,
        };
      },
    });

    createNodeCommentMutation.mutate(
      {
        nodeId,
        parentId: comment.commentId,
        body: submittedReplyBody,
        // Replies are never private — only root comments carry the `private` flag, and the
        // composer never rendered a privacy toggle to change it with.
        private: false,
      },
      {
        onSuccess: (createdComment) => {
          updateRepliesForParentThreadInCache({
            targetNodeId: nodeId,
            parentId: comment.commentId,
            updateThread: (thread) => {
              const hasCreatedComment = thread.latestReplies.some((reply) => reply.id === createdComment.id);
              const optimisticReplyIndex = thread.latestReplies.findIndex((reply) => reply.id === submittedReplyId);

              if (optimisticReplyIndex === -1) {
                if (hasCreatedComment) {
                  return thread;
                }

                return {
                  ...thread,
                  latestReplies: mergeRepliesById(thread.latestReplies, [createdComment]),
                  repliesCount: thread.repliesCount + 1,
                };
              }

              if (hasCreatedComment) {
                return {
                  ...thread,
                  latestReplies: thread.latestReplies.filter((reply) => reply.id !== submittedReplyId),
                  repliesCount: Math.max(thread.repliesCount - 1, 0),
                };
              }

              const nextLatestReplies = [...thread.latestReplies];
              nextLatestReplies.splice(optimisticReplyIndex, 1, createdComment);

              return {
                ...thread,
                latestReplies: mergeRepliesById(nextLatestReplies),
              };
            },
          });

          if (visibleRepliesCount > DEFAULT_VISIBLE_REPLIES_COUNT) {
            setVisibleRepliesCount((currentVisibleRepliesCount) => currentVisibleRepliesCount + 1);
          }
        },
        onError: (error) => {
          updateRepliesForParentThreadInCache({
            targetNodeId: nodeId,
            parentId: comment.commentId,
            updateThread: (thread) => {
              const hasOptimisticReply = thread.latestReplies.some((reply) => reply.id === submittedReplyId);

              if (!hasOptimisticReply) {
                return thread;
              }

              return {
                ...thread,
                latestReplies: thread.latestReplies.filter((reply) => reply.id !== submittedReplyId),
                repliesCount: Math.max(thread.repliesCount - 1, 0),
              };
            },
          });

          const restoredReplyCreatedAt = new Date().toISOString();
          const restoredReplyId = `${OPTIMISTIC_REPLY_ID_PREFIX}${comment.commentId}-${Date.now()}`;

          setPendingReplyCloseId(null);
          setReplyDraft(submittedReplyBody);
          replyDraftCreatedAtRef.current = restoredReplyCreatedAt;
          replyDraftIdRef.current = restoredReplyId;
          setActiveReplyComposerCommentId(comment.commentId);

          if (error.status === 404) {
            const queryCache = queryClient.getQueryCache();
            const queries = queryCache.findAll({
              queryKey: [QueryKey.NodeCommentList, nodeId, shareConfig?.id],
              exact: false,
            });

            queries.forEach((query) => {
              queryClient.setQueryData<InfiniteData<GetNodeCommentsResponse>>(query.queryKey, (oldData) => {
                if (!oldData) {
                  return oldData;
                }

                return {
                  ...oldData,
                  pages: oldData.pages.map((page) => {
                    const hasParentComment = page.comments.some(
                      (nodeComment) => nodeComment.commentId === comment.commentId,
                    );

                    if (!hasParentComment) {
                      return page;
                    }

                    return {
                      ...page,
                      comments: page.comments.filter((nodeComment) => nodeComment.commentId !== comment.commentId),
                      pagination: {
                        ...page.pagination,
                        count: Math.max(page.pagination.count - 1, 0),
                      },
                    };
                  }),
                };
              });
            });
          }
        },
      },
    );
  };

  return (
    <div id={comment.commentId} key={comment.commentId} className="flex flex-col gap-2">
      <div
        className={cn(
          'group/comment flex flex-col overflow-hidden rounded-[20px] transition-all duration-150',
          isEditingRoot || isEditingReply || isReplyComposerOpen
            ? localPrivate
              ? 'bg-feature-sharing/20'
              : 'bg-ui-light'
            : 'bg-ui-panel',
          {
            'opacity-50': isDeletingRoot,
          },
        )}
        data-actions-pinned={isPinnedByOwnDialog || undefined}
      >
        <CommentItem
          comment={rootComment}
          nodeId={comment.nodeId}
          rootMeta={{
            commentId: comment.commentId,
            resolvedAt: comment.resolvedAt,
            resolvedBy: comment.resolvedBy,
          }}
          isReply={false}
          readOnly={readOnly}
          onReply={isRootTombstone ? undefined : openReplyComposer}
          showMore={shouldShowMoreRepliesAction}
          canEdit={!isRootTombstone && !readOnly && Boolean(userId) && userId === comment.comment.createdBy?.id}
          canDelete={!isRootTombstone && canDeleteComment(comment.comment.createdBy)}
          canResolve={
            !isRootTombstone &&
            (isGuest
              ? Boolean(userId) && userId === comment.comment.createdBy?.id
              : isAuthenticatedUser && isDefined(commentAuthorRole) && commentAuthorRole !== 'guest')
          }
          isTombstone={isRootTombstone}
          deleteCommentId={isRootTombstone ? undefined : comment.commentId}
          onDeleteStart={() => setOpenCommentMenuId(null)}
          onDialogOpen={setPinnedDialogKey}
          onTogglePrivacy={isRootTombstone || readOnly || shareConfig?.id ? undefined : handleTogglePrivacy}
          isEditing={isEditingRoot}
          isMenuOpen={openCommentMenuId === comment.commentId}
          isSaving={updateCommentMutation.isPending}
          isDeleting={isDeletingRoot}
          normalizedDraft={normalizedRootDraft}
          isDraftEmpty={normalizedRootDraft.trim().length === 0}
          isDraftUnchanged={normalizedRootDraft === normalizedBody}
          repliesLength={renderedRepliesCount}
          itemRef={rootItemRef}
          scrollContainerRef={scrollContainerRef}
          editorRef={editorRef}
          isPrivate={localPrivate}
          onMenuOpenChange={(isOpen) => setOpenCommentMenuId(isOpen ? comment.commentId : null)}
          onStartEdit={() =>
            startEditingComment({
              commentId: comment.commentId,
              body: normalizedBody,
            })
          }
          onCancelEdit={() => cancelEditingComment(comment.commentId)}
          onSaveEdit={() =>
            handleSaveCommentEdit({
              commentId: comment.commentId,
              originalBody: normalizedBody,
              isPrivate: localPrivate,
              onSuccess: (updatedComment) => {
                setLocalBody(updatedComment.body ?? '');
                setLocalUpdatedAt(updatedComment.updatedAt);
              },
            })
          }
          onEditorInput={(element) =>
            syncCommentDraftFromElement({
              element,
              setDraft: (draft) => setCommentDraft(comment.commentId, draft),
            })
          }
          onEditorPaste={(event) =>
            handleCommentPaste({
              event,
              setDraft: (draft) => setCommentDraft(comment.commentId, draft),
            })
          }
          onEmojiInsert={(element) =>
            syncCommentDraftFromElement({
              element,
              setDraft: (draft) => setCommentDraft(comment.commentId, draft),
            })
          }
        />

        {(shouldShowMoreRepliesAction || visibleReplies.length > 0 || isReplyComposerOpen) && (
          <>
            {shouldShowMoreRepliesAction && (
              <CommentShowMoreSeparator
                onClick={showMoreReplies}
                disabled={fetchRepliesMutation.isPending}
                loading={fetchRepliesMutation.isPending}
                className={cn({
                  'mb-2': isFirstVisibleReplyBeingEdited,
                  'mt-2': isEditingRoot,
                })}
              >
                {tComments('moreReplies', { count: nextLoadRepliesCount })}
              </CommentShowMoreSeparator>
            )}

            {visibleReplies.map((reply, index) => {
              const isReplyTombstone = Boolean(reply.tombstone?.isDeleted);
              const isOptimisticReply = isOptimisticReplyId(reply.id);
              const normalizedReplyBody = normalizeCommentBody(reply.body ?? '');
              const normalizedReplyEditDraft = normalizeCommentBody(draftByCommentId[reply.id] ?? normalizedReplyBody);

              return (
                <CommentItem
                  key={reply.id}
                  index={index}
                  comment={reply}
                  nodeId={comment.nodeId}
                  isReply
                  isPrivate={localPrivate}
                  readOnly={readOnly}
                  onReply={isReplyTombstone || isOptimisticReply ? undefined : openReplyComposer}
                  repliesLength={renderedRepliesCount}
                  canEdit={
                    !isOptimisticReply &&
                    !isReplyTombstone &&
                    !readOnly &&
                    Boolean(userId) &&
                    userId === reply.createdBy?.id
                  }
                  canDelete={!isOptimisticReply && !isReplyTombstone && canDeleteComment(reply.createdBy)}
                  isTombstone={isReplyTombstone}
                  deleteCommentId={isReplyTombstone || isOptimisticReply ? undefined : reply.id}
                  parentCommentId={comment.commentId}
                  onDeleteStart={() => setOpenCommentMenuId(null)}
                  onDialogOpen={setPinnedDialogKey}
                  onDeleteSuccess={handleReplyDeleteSuccess}
                  isEditing={!isOptimisticReply && !isReplyTombstone && editingCommentId === reply.id}
                  isMenuOpen={!isOptimisticReply && openCommentMenuId === reply.id}
                  isSaving={isOptimisticReply || updateCommentMutation.isPending}
                  isDeleting={deletingCommentId === reply.id}
                  normalizedDraft={normalizedReplyEditDraft}
                  isDraftEmpty={normalizedReplyEditDraft.trim().length === 0}
                  isDraftUnchanged={normalizedReplyEditDraft === normalizedReplyBody}
                  itemRef={getReplyItemRef(reply.id)}
                  scrollContainerRef={scrollContainerRef}
                  editorRef={getReplyEditorRef(reply.id)}
                  onMenuOpenChange={(isOpen) => setOpenCommentMenuId(isOpen ? reply.id : null)}
                  onStartEdit={() =>
                    startEditingComment({
                      commentId: reply.id,
                      body: normalizedReplyBody,
                    })
                  }
                  onCancelEdit={() => cancelEditingComment(reply.id)}
                  onSaveEdit={() =>
                    handleSaveCommentEdit({
                      commentId: reply.id,
                      originalBody: normalizedReplyBody,
                      // Replies are never private — only root comments carry the `private` flag.
                      isPrivate: false,
                    })
                  }
                  onEditorInput={(element) =>
                    syncCommentDraftFromElement({
                      element,
                      setDraft: (draft) => setCommentDraft(reply.id, draft),
                    })
                  }
                  onEditorPaste={(event) =>
                    handleCommentPaste({
                      event,
                      setDraft: (draft) => setCommentDraft(reply.id, draft),
                    })
                  }
                  onEmojiInsert={(element) =>
                    syncCommentDraftFromElement({
                      element,
                      setDraft: (draft) => setCommentDraft(reply.id, draft),
                    })
                  }
                />
              );
            })}

            {draftReply && (
              <CommentItem
                key={draftReply.id}
                index={visibleReplies.length}
                comment={draftReply}
                nodeId={comment.nodeId}
                isReply
                isPrivate={localPrivate}
                readOnly={readOnly}
                repliesLength={renderedRepliesCount}
                canEdit={false}
                canDelete={false}
                isDraftReply
                isEditing={isReplyComposerOpen}
                isMenuOpen={false}
                isSaving={createNodeCommentMutation.isPending}
                isDeleting={false}
                normalizedDraft={normalizedReplyDraft}
                isDraftEmpty={normalizedReplyDraft.trim().length === 0}
                isDraftUnchanged={false}
                itemRef={replyDraftItemRef}
                scrollContainerRef={scrollContainerRef}
                editorRef={replyEditorRef}
                onMenuOpenChange={() => undefined}
                onStartEdit={() => undefined}
                onCancelEdit={closeReplyComposer}
                onSaveEdit={handleSubmitReply}
                onEditorInput={(element) =>
                  syncCommentDraftFromElement({
                    element,
                    setDraft: setReplyDraft,
                  })
                }
                onEditorPaste={(event) =>
                  handleCommentPaste({
                    event,
                    setDraft: setReplyDraft,
                  })
                }
                onEmojiInsert={(element) =>
                  syncCommentDraftFromElement({
                    element,
                    setDraft: setReplyDraft,
                  })
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
